<template>
    <div class="container" v-if="transaction" :key="transaction.id">
        <v-title :breadcrumb="breadcrumb">
            {{ transaction.id }}
        </v-title>
        <div class="grid">
            <div class="grid-item grid-item-primary ">
                <transaction-summary
                    :transaction="transaction"
                    :is-processing="isLoading"
                    @action-starts="childActionStarts"
                    @action-ends="childActionEnds"/>
                <timeline
                    ref="timeline"
                    expanded
                    :key="`transaction-${transaction.id}-timeline`"
                    type="transaction"
                    :entity="transaction"/>
            </div>
            <div class="grid-item grid-item-secondary">
                <div class="grid">
                    <div class="grid-item grid-item-full">
                        <risk-analysis :model="transaction"/>
                    </div>
                    <div class="grid-item grid-item-full" v-if="$auth.readPaymentInstruments.isAllowed">
                        <div class="tile">
                            <div class="tile-title">
                                <h2>Payment Instrument</h2>
                            </div>
                            <v-payment-instrument
                                :instrument="paymentInstrument"
                                class="tile-content">
                                <p slot="noInstrument" class="text-muted">This transaction used
                                    <strong>{{paymentMethod}}</strong> as a payment method.</p>
                            </v-payment-instrument>
                        </div>
                    </div>
                    <div class="grid-item grid-item-full" v-if="$auth.customers.read.isAllowed">
                        <compact-customer-summary
                            :entry="transaction.customer"
                            :expand="transaction">
                            <template slot="additional-information">
                                <div class="tile-title">
                                    <h3>Primary Contact</h3>
                                </div>
                                <div class="tile-content">
                                    <v-contact @email-sent="$refs.timeline.update()" email-actions :contact="transaction.customer.primaryAddress"/>
                                </div>
                                <div class="tile-title">
                                    <h3>Billing Address</h3>
                                </div>
                                <div class="tile-content">
                                    <v-contact @email-sent="$refs.timeline.update()" email-actions :contact="transaction.billingAddress"/>
                                </div>
                            </template>
                        </compact-customer-summary>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import vTitle from '@/components/v-title';
    import vContact from '@/components/v-contact';
    import LoadStateMixin from '@/mixins/load-state-mixin';
    import TransactionModel from '@/common/models/transaction-model';
    import vPaymentInstrument from '@/components/payment-instrument/v-payment-instrument';
    import CompactCustomerSummary from '@/components/compact-customer-summary/compact-customer-summary';
    import PaymentInstrumentModel from '@/common/models/payment-instrument-model';
    import TransactionSummary from './components/transaction-summary';
    import RiskAnalysis from '../../components/risk-analysis/risk-analysis';

    const Timeline = () => import('@/components/timeline/timeline.vue');

    export default {
        name: 'transaction-details',
        components: {
            vTitle,
            vContact,
            vPaymentInstrument,
            CompactCustomerSummary,
            TransactionSummary,
            RiskAnalysis,
            Timeline,
        },
        mixins: [LoadStateMixin],
        props: {
            id: {
                type: [String, Number],
                default: null,
            },
        },
        computed: {
            paymentMethod() {
                return this.transaction
                    && this.transaction.paymentInstrument
                    && this.transaction.paymentInstrument.method;
            },
            paymentInstrument() {
                if (!this.paymentMethod) {
                    return false;
                }
                switch (this.paymentMethod) {
                    case PaymentInstrumentModel.Methods.ach:
                        return this.transaction.bankAccount;
                    case PaymentInstrumentModel.Methods.paymentCard:
                        return this.transaction.paymentCard;
                    case PaymentInstrumentModel.Methods.paypal:
                    case PaymentInstrumentModel.Methods.cash:
                        return this.transaction.paymentInstrument;
                    default:
                        return false;
                }
            },
            breadcrumb() {
                return {
                    label: 'Transaction Details',
                    route: {
                        name: 'transaction-details',
                        params: {
                            id: this.transaction.id,
                        },
                    },
                };
            },
        },
        data() {
            return {
                transaction: null,
                reloadOnParam: 'id',
            };
        },
        methods: {
            childActionStarts() {
                this.startLoadingPage();
            },
            async childActionEnds(result) {
                try {
                    // Reload transaction whenever an action was performed
                    this.transaction = await this.fetchTransaction();
                    this.$toast[result.type](result.message);
                    this.$refs.timeline.update();
                } catch (error) {
                    this.$toast.negative(`Transaction could not be reloaded on action: ${result.message}`);
                } finally {
                    this.stopLoadingPage();
                }
            },
            async fetchTransaction() {
                const params = {
                    id: this.id,
                    expand: 'paymentCard, bankAccount, customer, website',
                };
                const {fields} = await this.$api(
                    ({transactions}) => transactions.get(params),
                );
                const transaction = new TransactionModel(fields);
                const expandedAttributes = await Promise.all(this.expandTransaction(transaction));
                Object.assign(transaction, ...expandedAttributes);
                return transaction;
            },
            expandTransaction(transaction) {
                const conditionalExpansion = [];
                if (transaction.canRefund) {
                    conditionalExpansion.push(
                        this.fetchRefundedTransactions(transaction),
                    );
                }
                if (transaction.hasBeenRetried && !transaction.isRetry) {
                    conditionalExpansion.push(
                        this.fetchLastRetriedTransaction(transaction.id),
                    );
                }
                return conditionalExpansion;
            },
            /**
             * Fetch child refunded transactions required to calculate
             * the available amount to refund on the parent transaction.
             * @param {TransactionModel} parentTransaction
             * @return {Promise<{retriedTransactions}>}
             */
            async fetchRefundedTransactions({id, customerId}) {
                const params = {
                    filter: `customerId:${customerId};parentTransactionId:${id};result:approved;type:refund,void`,
                };
                const {items} = await this.$api(({transactions}) => transactions.getAll(params));
                const refundedTransactions = items.length ? items.map(({fields}) => new TransactionModel(fields)) : null;
                return {refundedTransactions};
            },
            /**
             * Fetch the last retried transaction
             * that were generated from an initial declined transaction.
             * @param {String} initialDeclinedTransactionId
             * @return {Promise<{retriedTransactions}>}
             */
            async fetchLastRetriedTransaction(initialDeclinedTransactionId) {
                const params = {
                    filter: `retriedTransactionId:${initialDeclinedTransactionId}`,
                    sort: '-createdTime',
                    limit: 1,
                };
                const {items} = await this.$api(
                    ({transactions}) => transactions.getAll(params),
                );
                const [lastRetry] = items;
                return {lastRetry: lastRetry ? new TransactionModel(lastRetry.fields) : null};
            },
            async fetchData() {
                try {
                    this.transaction = await this.fetchTransaction();
                } catch (error) {
                    this.$toast.fromError(error, 'Unable to find the transaction you requested.');
                    this.$redirectOnError(error, {name: 'transactions'});
                }
            },
        },
    };
</script>
